import React, { FC, MouseEvent, useCallback, useMemo, useRef, useState } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  getRelativeCoordinate,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { Box, Token } from '@revolut-internal/ui-kit'

import {
  ADDITIONAL_DATA_KEY,
  DEFAULT_LINE_CHART_HEIGHT,
  MAIN_DATA_KEY,
  X_AXIS_LABEL_KEY,
} from 'consts/chart'
import { ChartItem, ChartProps } from 'types'
import { formatNumber } from 'utils/formatNumber'

import { ChartLegend } from '../ChartLegend'

import { calculateAdditionLabelPosition } from './calculateAdditionLabelPosition'
import { HoveredLine, HoveredPoint, isSameHoveredPoint } from './findHoveredPoint'
import {
  LineChartHoverTracker,
  LineChartHoverTrackerHandle,
} from './LineChartHoverTracker'
import { LineChartPlaceholder } from './LineChartPlaceholder'
import { renderAdditionalLine } from './renderAdditionalLine'
import { renderMainLine } from './renderMainLine'

const TICK_STYLE = { fill: Token.color.greyTone50, fontSize: '12px' }

const LEFT_MARGIN = 12
const RIGHT_MARGIN = 10
const TOP_MARGIN = 20
const NO_LEGEND_BOTTOM_MARGIN = 4

const LEGEND_HEIGHT = 42
const X_AXIS_HEIGHT = 32

const generateHorizontalPoints = (height: number, hasLegend = true) =>
  Array.from(
    { length: 8 },
    (_, index) =>
      (index *
        (height -
          TOP_MARGIN -
          (hasLegend ? LEGEND_HEIGHT : NO_LEGEND_BOTTOM_MARGIN) -
          X_AXIS_HEIGHT)) /
        7 +
      TOP_MARGIN,
  )

const formatYAxisTick = (tick: number) => String(formatNumber(tick) ?? tick)

export const LineChart: FC<ChartProps> = ({
  data,
  charts,
  additionCharts = [],
  primaryLabel,
  additionLabel,
  selectedCharts,
  height = DEFAULT_LINE_CHART_HEIGHT,
  entityName,
}) => {
  const hoverTrackerRef = useRef<LineChartHoverTrackerHandle>(null)
  const handleChartMouseMove = useCallback(
    (_: unknown, event: MouseEvent<SVGGraphicsElement>) => {
      hoverTrackerRef.current?.setPointerX(getRelativeCoordinate(event).relativeX)
    },
    [],
  )
  const [hoveredPoint, setHoveredPoint] = useState<HoveredPoint>()
  const handleHoveredPointChange = useCallback((nextPoint?: HoveredPoint) => {
    setHoveredPoint(prevPoint =>
      isSameHoveredPoint(prevPoint, nextPoint) ? prevPoint : nextPoint,
    )
  }, [])
  const [hoveredLine, setHoveredLine] = useState<HoveredLine>()
  const handleMainLineHighlight = useCallback((name?: string) => {
    setHoveredLine(name === undefined ? undefined : { name, isAddition: false })
  }, [])
  const handleAdditionLineHighlight = useCallback((name?: string) => {
    setHoveredLine(name === undefined ? undefined : { name, isAddition: true })
  }, [])
  const handleChartMouseLeave = useCallback(() => {
    setHoveredLine(undefined)
  }, [])
  const isHoverTrackable = (chart: ChartItem) =>
    !hoveredLine || chart.name === hoveredLine.name

  const highlightedLine = hoveredPoint ?? hoveredLine
  const activeLineName = highlightedLine?.name
  const additionActiveLineName = highlightedLine?.isAddition
    ? highlightedLine.name
    : undefined
  const getTooltipPayload = (chartValue: string) =>
    hoveredPoint?.chartValue === chartValue ? hoveredPoint.payload : undefined

  const chartsWithSelectionStatus = charts.map(bar => ({
    ...bar,
    selected: !!selectedCharts?.includes(bar.value),
  }))
  const additionChartsWithSelectionStatus = additionCharts.map(bar => ({
    ...bar,
    selected: !!selectedCharts?.includes(bar.value),
  }))
  const selectedChartsLength = chartsWithSelectionStatus.filter(
    ({ selected }) => selected,
  ).length
  const selectedAdditionChartsLength = additionChartsWithSelectionStatus.filter(
    ({ selected }) => selected,
  ).length
  const allChartsWithSelectionStatus = useMemo(
    () => [...chartsWithSelectionStatus, ...additionChartsWithSelectionStatus],
    [chartsWithSelectionStatus, additionChartsWithSelectionStatus],
  )

  const hasLegend = Boolean(chartsWithSelectionStatus.length)
  const horizontalPoints = generateHorizontalPoints(height, hasLegend)

  const yValues = useMemo(
    () =>
      allChartsWithSelectionStatus
        .filter(({ selected }) => selected)
        .flatMap(bar =>
          data.map(
            item =>
              item[ADDITIONAL_DATA_KEY]?.[bar.value]?.value ??
              item[MAIN_DATA_KEY]?.[bar.value]?.value ??
              0,
          ),
        ),
    [data, allChartsWithSelectionStatus],
  )
  const yMax = Math.max(...yValues)
  const yMin = Math.min(...yValues)
  const yTicks = useMemo(() => [yMin, yMax], [yMin, yMax])
  const chartMargin = useMemo(
    () => ({
      top: TOP_MARGIN,
      right: -RIGHT_MARGIN,
      left: LEFT_MARGIN,
      bottom: hasLegend ? 0 : NO_LEGEND_BOTTOM_MARGIN,
    }),
    [hasLegend],
  )

  if (!data.length) {
    return <LineChartPlaceholder height={height} />
  }

  return (
    <Box>
      <ResponsiveContainer height={height} width="100%">
        <ComposedChart
          height={height}
          data={data}
          margin={chartMargin}
          onMouseMove={handleChartMouseMove}
          onMouseLeave={handleChartMouseLeave}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            horizontalPoints={horizontalPoints}
          />
          <XAxis
            dataKey={X_AXIS_LABEL_KEY}
            tick={TICK_STYLE}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={10}
            tickMargin={20}
            height={X_AXIS_HEIGHT}
          />
          <YAxis
            ticks={yTicks}
            interval="preserveStartEnd"
            orientation="right"
            tick={TICK_STYLE}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatYAxisTick}
          />

          {hasLegend && (
            <Legend
              height={LEGEND_HEIGHT}
              content={
                <ChartLegend
                  items={chartsWithSelectionStatus}
                  containerProps={{ pr: RIGHT_MARGIN }}
                />
              }
            />
          )}

          {chartsWithSelectionStatus.map(chart =>
            renderMainLine({
              chart,
              primaryLabel,
              additionLabel,
              activeLineName,
              additionActiveLineName,
              entityName,
              tooltipPayload: getTooltipPayload(chart.value),
              showDots: selectedChartsLength <= 1,
              onHighlight: handleMainLineHighlight,
            }),
          )}

          {additionChartsWithSelectionStatus.map(chart =>
            renderAdditionalLine({
              chart,
              additionLabel,
              activeLineName,
              additionActiveLineName,
              additionLabelPosition: calculateAdditionLabelPosition(data, chart.value),
              entityName,
              tooltipPayload: getTooltipPayload(chart.value),
              showLabel: selectedAdditionChartsLength === 1,
              onHighlight: handleAdditionLineHighlight,
            }),
          )}

          <LineChartHoverTracker
            ref={hoverTrackerRef}
            charts={chartsWithSelectionStatus.filter(isHoverTrackable)}
            additionCharts={additionChartsWithSelectionStatus.filter(isHoverTrackable)}
            onChange={handleHoveredPointChange}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
